import { useEffect, useState } from "react";
import { X, Loader2, Save, Link as LinkIcon, MessageCircle, Copy, RefreshCw, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatReceivedAt } from "../../lib/dateFormat";
import AdminMessagesPanel from "./AdminMessagesPanel";
import SchedulePanel from "./SchedulePanel";
import DemoOutcomePanel from "./DemoOutcomePanel";
import { useBodyScrollLock } from "../../lib/useBodyScrollLock";

/**
 * V156 — Assignment detail modal. Tabs: Overview / Student & Parent / Tutor / Activity Log.
 * Package + workflow-status edits happen inline via the Overview + Package cards.
 *
 * Deliberately does NOT show parent phone/email in the Tutor tab so future
 * tutor-portal wiring can reuse the same modal without a PII redaction pass.
 */

const STATUS_OPTIONS = [
    "tutor_accepted",
    "awaiting_admin_setup",
    "awaiting_parent_link",
    "awaiting_tutor_link",
    "awaiting_schedule_confirm",
    "demo_scheduled",
    "demo_completed",
    "awaiting_parent_approval",
    "active",
    "paused",
    "completed",
    "renewal_due",
    "cancelled",
    "sync_error",
];

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "sessions", label: "Sessions" },
    { id: "portal-links", label: "Portal Links" },
    { id: "messages", label: "Messages" },
    { id: "student", label: "Student & Parent" },
    { id: "tutor", label: "Tutor" },
    { id: "activity", label: "Activity Log" },
];

export default function AssignmentDetailModal({ assignment, onClose, onSaved }) {
    const [tab, setTab] = useState("overview");
    const [detail, setDetail] = useState(null);
    const [edit, setEdit] = useState({});
    const [saving, setSaving] = useState(false);
    // V160.4 — Unread badge on the Messages tab. Independent of the Messages
    // panel poll: we want the dot to appear even when the tab isn't open yet.
    const [unread, setUnread] = useState(0);
    useBodyScrollLock(true);

    const reloadDetail = () => {
        api.get(`/admin/assignments/${assignment.id}`)
            .then((r) => setDetail(r.data))
            .catch(() => { /* keep last snapshot on transient failure */ });
    };

    const reloadUnread = () => {
        api.get(`/admin/assignments/${assignment.id}/messages/unread-count?_=${Date.now()}`)
            .then((r) => setUnread(r.data?.unread ?? 0))
            .catch(() => {});
    };

    useEffect(() => {
        reloadDetail();
        reloadUnread();
        // Refresh unread every 15s while the modal is open. Clicking the
        // Messages tab will trigger a full GET, which auto-marks-read
        // server-side and drops the count on the next poll (or immediately
        // via the local setter below).
        const iv = setInterval(reloadUnread, 15000);
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignment.id]);

    // When the admin opens the Messages tab, the panel's GET auto-clears
    // the server-side read state — mirror that locally so the dot vanishes
    // instantly without waiting for the next 15s poll.
    useEffect(() => {
        if (tab === "messages" && unread > 0) {
            setUnread(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const patch = (updates) => setEdit((prev) => ({ ...prev, ...updates }));

    const savePackage = async () => {
        if (Object.keys(edit).length === 0) return;
        setSaving(true);
        try {
            await api.patch(`/admin/assignments/${assignment.id}`, edit);
            // Refetch so activity_log (and any auto-advanced status) is fresh.
            reloadDetail();
            setEdit({});
            toast.success("Assignment updated");
            onSaved?.();
        } catch (_e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const changeStatus = async (status) => {
        try {
            await api.patch(`/admin/assignments/${assignment.id}/status`, { status });
            // Refetch so activity_log reflects the new status_changed entry.
            reloadDetail();
            toast.success("Status updated");
            onSaved?.();
        } catch (_e) {
            toast.error("Status update failed");
        }
    };

    if (!detail) {
        return (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="assignment-detail-modal">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
        );
    }

    const view = { ...detail, ...edit };

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            style={{ width: "100vw", height: "100vh" }}
            role="dialog"
            aria-modal="true"
            data-testid="assignment-detail-modal"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
        >
            <div
                className="bg-white w-full sm:w-auto sm:max-w-3xl sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Assignment</div>
                        <div className="text-lg sm:text-xl font-heading font-bold text-[#0F172A] truncate">{view.assignment_code}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">BMT: {view.bookmytutor_lead_id}{view.inquiry_id ? ` · Inquiry: ${view.inquiry_id.slice(0, 8)}…` : ""}</div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-600" data-testid="assignment-detail-close" data-cm-compact aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-slate-200 px-3 sm:px-6 overflow-x-auto" data-testid="assignment-detail-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`assignment-tab-${t.id}`}
                            data-active={tab === t.id ? "true" : "false"}
                            data-cm-compact
                            className={`relative px-3 py-3 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 -mb-px min-h-[44px] ${
                                tab === t.id
                                    ? "border-cyan-500 text-[#0F172A]"
                                    : "border-transparent text-slate-500 hover:text-slate-800"
                            }`}
                        >
                            {t.label}
                            {t.id === "messages" && unread > 0 && (
                                <span
                                    data-testid="assignment-tab-messages-unread"
                                    aria-label={`${unread} unread message${unread === 1 ? "" : "s"}`}
                                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
                                >
                                    {unread > 9 ? "9+" : unread}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="p-4 sm:p-6 space-y-5">
                    {/* Overview */}
                    {tab === "overview" && (
                        <>
                            {view.is_demo && (
                                <DemoOutcomePanel assignment={view} onChanged={onSaved} />
                            )}
                            <StatusBar current={view.status} onChange={changeStatus} />
                            <SectionCard title="Class Configuration">
                                <NumberField label="Total sessions" value={view.total_sessions ?? ""} onChange={(v) => patch({ total_sessions: v })} testid="pkg-total-sessions" />
                                <NumberField label="Session duration (min)" value={view.session_duration_minutes ?? ""} onChange={(v) => patch({ session_duration_minutes: v })} testid="pkg-session-duration" />
                                <NumberField label="Sessions / week" value={view.sessions_per_week ?? ""} onChange={(v) => patch({ sessions_per_week: v })} testid="pkg-sessions-per-week" />
                                <TextField label="Preferred days" value={view.preferred_days || ""} onChange={(v) => patch({ preferred_days: v })} placeholder="Mon, Wed, Fri" />
                                <TextField label="Preferred time" value={view.preferred_time || ""} onChange={(v) => patch({ preferred_time: v })} placeholder="5:30 PM" />
                                <TextField label="Start date" value={view.start_date || ""} onChange={(v) => patch({ start_date: v })} placeholder="YYYY-MM-DD" />
                                <NumberField label="Package fee (₹)" value={view.package_fee ?? ""} onChange={(v) => patch({ package_fee: v })} step="0.01" />
                                <NumberField label="Tutor rate (₹)" value={view.tutor_rate ?? ""} onChange={(v) => patch({ tutor_rate: v })} step="0.01" />
                            </SectionCard>
                            <SectionCard title="Internal coordinator notes">
                                <textarea
                                    value={view.coordinator_notes || ""}
                                    onChange={(e) => patch({ coordinator_notes: e.target.value })}
                                    data-testid="pkg-notes"
                                    rows={3}
                                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                                />
                            </SectionCard>
                            {Object.keys(edit).length > 0 && (
                                <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white border-t border-slate-200 flex justify-end gap-2">
                                    <button onClick={() => setEdit({})} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Discard</button>
                                    <button
                                        onClick={savePackage}
                                        disabled={saving}
                                        data-testid="pkg-save-btn"
                                        data-cm-primary
                                        className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60"
                                    >
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                        Save
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {tab === "sessions" && (
                        <SchedulePanel assignmentId={assignment.id} />
                    )}

                    {tab === "portal-links" && (
                        <PortalLinksPanel
                            assignmentId={assignment.id}
                            studentName={view.student_name}
                            tutorName={view.tutor_name}
                            onChange={reloadDetail}
                            onSaved={onSaved}
                        />
                    )}

                    {tab === "student" && (
                        <>
                            <SectionCard title="Student">
                                <ReadField label="Name" value={view.student_name} />
                                <ReadField label="Grade" value={view.grade} />
                                <ReadField label="Board" value={view.board} />
                                <ReadField label="Curriculum" value={view.curriculum} />
                                <ReadField label="Subject" value={view.subject} />
                                <ReadField label="School" value={view.school} />
                                <ReadField label="City" value={view.city} />
                                <ReadField label="State" value={view.state} />
                                <ReadField label="Country" value={view.country} />
                                <ReadField label="Time zone" value={view.timezone} />
                                <ReadField label="Class mode" value={view.class_mode} />
                            </SectionCard>
                            <SectionCard title="Parent — internal only (never shared with tutor)">
                                <ReadField label="Parent name" value={view.parent_name} />
                                <ReadField label="Parent email" value={view.parent_email} />
                                <ReadField label="Parent phone" value={view.parent_phone} />
                                <p className="text-[11px] text-slate-500 mt-1 col-span-2">Parent contact is stored only on this admin record and is intentionally excluded from every tutor-side view (portal, messages, share links) by server-side projection.</p>
                            </SectionCard>
                        </>
                    )}

                    {tab === "tutor" && (
                        <>
                            <SectionCard title="Tutor">
                                <ReadField label="Tutor ID" value={view.tutor_id} />
                                <ReadField label="Name" value={view.tutor_name} />
                                <ReadField label="Qualification" value={view.tutor_qualification} />
                                <ReadField label="Experience (years)" value={view.tutor_experience_years} />
                                <ReadField label="Specialization" value={view.tutor_specialization} />
                                <ReadField label="BMT acceptance" value={formatReceivedAt(view.acceptance_timestamp)} />
                            </SectionCard>
                            <p className="text-[11px] text-slate-500">Tutor contact is intentionally NOT stored on the Anand Tutorials assignment — all tutor communication continues to flow through BookMyTutor and the Phase-2 secure tutor portal.</p>
                        </>
                    )}

                    {tab === "activity" && (
                        <SectionCard title="Activity Log">
                            {(view.activity_log || []).length === 0 ? (
                                <div className="text-sm text-slate-500 col-span-2">No activity yet.</div>
                            ) : (
                                <ul className="col-span-2 divide-y divide-slate-100 -mx-2">
                                    {(view.activity_log || []).map((e) => (
                                        <li key={e.id} className="px-2 py-2">
                                            <div className="text-xs font-semibold text-slate-800">{humanAction(e.action)} <span className="font-normal text-slate-500">— by {e.actor}</span></div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">{formatReceivedAt(e.timestamp)}</div>
                                            {e.new_value && typeof e.new_value === "object" && (
                                                <pre className="text-[10px] text-slate-500 mt-1 bg-slate-50 p-1.5 rounded overflow-x-auto">{JSON.stringify(e.new_value, null, 2)}</pre>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </SectionCard>
                    )}

                    {tab === "messages" && (
                        <AdminMessagesPanel assignment={view} />
                    )}
                </div>
            </div>
        </div>
    );
}

function humanAction(a) {
    return ({
        assignment_created: "Assignment created",
        assignment_updated: "Assignment updated",
        status_changed: "Status changed",
        status_auto_advanced: "Status auto-advanced",
    })[a] || a;
}

function StatusBar({ current, onChange }) {
    return (
        <SectionCard title="Workflow Status">
            <div className="col-span-2 flex flex-wrap items-center gap-2">
                <select
                    value={current}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid="assignment-status-select"
                    className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <span className="text-[11px] text-slate-500">Status changes are recorded in the Activity Log.</span>
            </div>
        </SectionCard>
    );
}

function SectionCard({ title, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">{title}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50/60 border border-slate-100 rounded-xl p-3 sm:p-4">
                {children}
            </div>
        </div>
    );
}

function ReadField({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-slate-400 font-semibold">{label}</div>
            <div className="text-sm text-slate-800 mt-0.5">{value || <span className="text-slate-300">—</span>}</div>
        </div>
    );
}

function TextField({ label, value, onChange, placeholder }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">{label}</div>
            <input
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
            />
        </div>
    );
}

function NumberField({ label, value, onChange, step = 1, testid }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">{label}</div>
            <input
                type="number"
                inputMode="decimal"
                step={step}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
                data-testid={testid}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
            />
        </div>
    );
}

// ------------------------------------------------------- Portal Links Panel

function PortalLinksPanel({ assignmentId, studentName, tutorName, onChange, onSaved }) {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(null);
    // Freshly-minted raw tokens live only in memory — never persisted. Once
    // the admin dismisses/refreshes we can only revoke/regenerate (never
    // recover the raw string), which matches the security spec.
    const [freshRaw, setFreshRaw] = useState({});

    const origin = typeof window !== "undefined" ? window.location.origin : "";

    const load = () => {
        // Cache-buster: browser HTTP cache was returning stale [] after POST.
        api.get(`/admin/assignments/${assignmentId}/portal-links?_=${Date.now()}`)
            .then((r) => setItems(r.data?.items || []))
            .catch(() => toast.error("Failed to load portal links"));
    };
    useEffect(load, [assignmentId]);

    const parentActive = items.find((t) => t.party === "parent" && !t.revoked_at);
    const tutorActive = items.find((t) => t.party === "tutor" && !t.revoked_at);

    const buildParentMsg = (url) => `Dear Parent,

Your Anand Tutorials class portal is ready.

Please use the secure link below to view your class schedule, join online sessions, check attendance, and track ${studentName || "your child"}'s learning progress.

${url}

— Team Anand Tutorials`;
    const buildTutorMsg = (url) => `Dear ${tutorName || "Tutor"},

Your assigned student workspace is ready.

Please access the secure link below to view class requirements, confirm schedules, conduct sessions, mark attendance, and submit class reports.

${url}

— Team Anand Tutorials`;

    const generate = async (party) => {
        setBusy(`gen-${party}`);
        try {
            const r = await api.post(`/admin/assignments/${assignmentId}/portal-links/${party}`, {});
            setFreshRaw((p) => ({ ...p, [party]: r.data }));
            // V157 fix — OPTIMISTIC in-place patch so the Regenerate label +
            // Revoke button appear atomically. The generated token doc must
            // shape-match what GET /portal-links returns: party, id, revoked_at.
            setItems((prev) => {
                const stripped = prev.filter((t) => !(t.party === party && !t.revoked_at));
                return [{
                    id: r.data.token_id,
                    party,
                    revoked_at: null,
                    revoked_by: null,
                    expires_at: r.data.expires_at,
                    created_at: new Date().toISOString(),
                    last_accessed_at: null,
                    access_count: 0,
                }, ...stripped];
            });
            // The POST response is authoritative — no follow-up GET needed.
            // (An earlier attempt raced the browser's HTTP cache and clobbered
            // the optimistic update; the POST already contains everything the
            // panel needs to render Regenerate + Revoke.)
            onChange?.(); onSaved?.();
            toast.success(`${party === "parent" ? "Parent" : "Tutor"} link generated`);
        } catch (_e) { toast.error("Failed to generate link"); }
        finally { setBusy(null); }
    };
    const revoke = async (tokenId) => {
        setBusy(`revoke-${tokenId}`);
        try {
            await api.post(`/admin/assignments/${assignmentId}/portal-links/${tokenId}/revoke`);
            // V157 fix — clear the freshly-shown raw URL card so the admin
            // doesn't keep sharing an already-dead link.
            setFreshRaw({});
            // Optimistic — mark the just-revoked token in-place so the
            // Revoke button hides and the card returns to "no active link".
            setItems((prev) => prev.map((t) => (
                t.id === tokenId ? { ...t, revoked_at: new Date().toISOString() } : t
            )));
            onChange?.(); onSaved?.();
            toast.success("Link revoked");
        } catch (_e) { toast.error("Revoke failed"); }
        finally { setBusy(null); }
    };
    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch (_e) { toast.error("Copy failed"); }
    };
    const whatsapp = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");

    return (
        <>
            <PartyCard
                party="parent"
                label="Parent Portal Link"
                fresh={freshRaw.parent}
                active={parentActive}
                origin={origin}
                onGenerate={() => generate("parent")}
                onRevoke={(id) => revoke(id)}
                onCopy={copy}
                onWhatsApp={whatsapp}
                busy={busy}
                buildMsg={buildParentMsg}
            />
            <PartyCard
                party="tutor"
                label="Tutor Portal Link"
                fresh={freshRaw.tutor}
                active={tutorActive}
                origin={origin}
                onGenerate={() => generate("tutor")}
                onRevoke={(id) => revoke(id)}
                onCopy={copy}
                onWhatsApp={whatsapp}
                busy={busy}
                buildMsg={buildTutorMsg}
            />
            <div className="text-[11px] text-slate-500 col-span-2 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <strong>Privacy:</strong> Parent's phone/email is NEVER shown in the tutor portal, and the tutor's contact is never shown to the parent. The full token is displayed only once at generation — after that, only regenerate (which revokes the previous one) or revoke.
            </div>
        </>
    );
}

function PartyCard({ party, label, fresh, active, origin, onGenerate, onRevoke, onCopy, onWhatsApp, busy, buildMsg }) {
    const url = fresh?.token ? `${origin}/portal/${party}/${fresh.token}` : null;
    return (
        <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
                {active ? (
                    <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Active</span>
                ) : (
                    <span className="text-[10px] text-slate-500">No active link</span>
                )}
            </div>
            <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-3 space-y-3">
                {fresh?.token ? (
                    <div>
                        <div className="text-[10px] uppercase text-emerald-700 font-semibold mb-1">New link — shown once. Save or share it now.</div>
                        <div className="bg-white border border-emerald-200 rounded px-2 py-1.5 text-[11px] font-mono break-all text-emerald-900" data-testid={`portal-${party}-url`}>{url}</div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <button onClick={() => onCopy(url, "Link")} data-testid={`portal-${party}-copy-link`} data-cm-compact className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-slate-200 px-2.5 py-1.5 rounded hover:border-cyan-300"><Copy className="w-3 h-3" /> Copy link</button>
                            <button onClick={() => onCopy(buildMsg(url), "Message")} data-testid={`portal-${party}-copy-msg`} data-cm-compact className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-slate-200 px-2.5 py-1.5 rounded hover:border-cyan-300"><Copy className="w-3 h-3" /> Copy message</button>
                            <button onClick={() => onWhatsApp(buildMsg(url))} data-testid={`portal-${party}-whatsapp`} data-cm-compact className="inline-flex items-center gap-1 text-xs font-semibold bg-[#25D366] text-white px-2.5 py-1.5 rounded hover:brightness-95"><MessageCircle className="w-3 h-3" /> WhatsApp</button>
                        </div>
                    </div>
                ) : active ? (
                    <div className="text-xs text-slate-600">
                        Active link created {formatReceivedAt(active.created_at)}. Expires {formatReceivedAt(active.expires_at)}.
                        {active.last_accessed_at && <> · Last opened {formatReceivedAt(active.last_accessed_at)}</>}
                        {typeof active.access_count === "number" && <> · Opened {active.access_count} time{active.access_count === 1 ? "" : "s"}</>}
                        <div className="text-[11px] text-amber-700 mt-1">The full URL is stored only in hashed form. Generate a new one to share again (this will revoke the current active one).</div>
                    </div>
                ) : (
                    <div className="text-xs text-slate-500">No portal link yet. Generate one to share with the {party}.</div>
                )}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={onGenerate}
                        disabled={busy === `gen-${party}`}
                        data-testid={`portal-${party}-generate`}
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0F172A] text-white px-3 py-2 rounded hover:bg-[#1E293B] disabled:opacity-60"
                    >
                        {busy === `gen-${party}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (active ? <RefreshCw className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />)}
                        {active ? "Regenerate" : "Generate"}
                    </button>
                    {active && (
                        <button
                            onClick={() => onRevoke(active.id)}
                            disabled={busy === `revoke-${active.id}`}
                            data-testid={`portal-${party}-revoke`}
                            data-cm-primary
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded hover:bg-rose-100 disabled:opacity-60"
                        >
                            {busy === `revoke-${active.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            Revoke
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

